import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import { useAgent } from "../../contexts/AgentContext";

const direction_style = "left";

// Israeli business days: Sun–Thu. Weekend = Fri (5) + Sat (6).
function businessDaysSince(deliveryDateStr) {
  if (!deliveryDateStr) return 0;
  const start = new Date(deliveryDateStr);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (today <= start) return 0;
  let count = 0;
  const current = new Date(start);
  while (current < today) {
    current.setDate(current.getDate() + 1);
    const day = current.getDay();
    if (day !== 5 && day !== 6) count++;
  }
  return count;
}

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const OrdersTable = React.memo(({ orders, onOpen }) => {
  if (orders.length === 0) return null;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              מספר הזמנה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              שם לקוח
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              מספר לקוח
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              תאריך אספקה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              סניף
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, idx) => {
            const date = order.STATUSDATE ? order.STATUSDATE.split("T")[0] : "";
            const days = businessDaysSince(date);
            const expired = days > 3;
            return (
              <TableRow key={order.ORDNAME || idx} hover>
                <TableCell sx={{ textAlign: direction_style }}>{order.ORDNAME}</TableCell>
                <TableCell sx={{ textAlign: direction_style }}>{order.CDES}</TableCell>
                <TableCell sx={{ textAlign: direction_style }}>{order.CUSTNAME}</TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {formatDate(order.STATUSDATE)}
                </TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {order.BRANCHNAME}
                </TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {expired ? (
                    <Tooltip
                      title={`לא ניתן לפתוח תלונה — עברו יותר מ-3 ימי עסקים מתאריך האספקה (${days} ימי עסקים חלפו)`}
                      arrow
                    >
                      <Box component="span" sx={{ display: "inline-block" }}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled
                          sx={{
                            backgroundColor: "#0563B0",
                            "&:hover": { backgroundColor: "#0454a0" },
                          }}
                        >
                          פתח תלונה
                        </Button>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onOpen(order)}
                      sx={{
                        backgroundColor: "#0563B0",
                        "&:hover": { backgroundColor: "#0454a0" },
                      }}
                    >
                      פתח תלונה
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

const OrdersTab = ({ orders, ordersLoading, ordersError }) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(val.length >= 2 ? val : "");
    }, 300);
  };

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = !q
      ? orders
      : orders.filter(
          (o) =>
            String(o.ORDNAME || "").toLowerCase().includes(q) ||
            String(o.CDES || "").toLowerCase().includes(q) ||
            String(o.CUSTNAME || "").toLowerCase().includes(q),
        );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    const getDay = (dateStr) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    return [...filtered].sort((a, b) => {
      const da = getDay(a.STATUSDATE);
      const db = getDay(b.STATUSDATE);

      const aIsToday = da === todayTime;
      const bIsToday = db === todayTime;
      const aIsFuture = da !== null && da > todayTime;
      const bIsFuture = db !== null && db > todayTime;

      // Today first
      if (aIsToday && !bIsToday) return -1;
      if (!aIsToday && bIsToday) return 1;

      // Future before past
      if (aIsFuture && !bIsFuture) return -1;
      if (!aIsFuture && bIsFuture) return 1;

      // Both future → ascending (soonest first)
      if (aIsFuture && bIsFuture) return (da ?? 0) - (db ?? 0);

      // Both past → descending (most recent first)
      return (db ?? 0) - (da ?? 0);
    });
  }, [searchQuery, orders]);

  const handleOpenComplaint = useCallback(
    (order) => {
      const date = order.STATUSDATE ? order.STATUSDATE.split("T")[0] : "";
      navigate(
        `/agent-complaints/new?orderNumber=${order.ORDNAME}&deliveryDate=${date}`,
        { state: { order } },
      );
    },
    [navigate],
  );

  if (ordersLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (ordersError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {ordersError}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          size="small"
          placeholder="חיפוש לפי מספר הזמנה / שם לקוח / מספר לקוח"
          value={inputValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 360, direction: "rtl" }}
        />
      </Box>

      {filteredOrders.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <AssignmentIcon
            sx={{ fontSize: 52, color: "text.disabled", mb: 1.5 }}
          />
          <Typography color="text.secondary">
            {orders.length === 0
              ? "לא נמצאו הזמנות ב-3 החודשים האחרונים"
              : "לא נמצאו הזמנות התואמות לחיפוש"}
          </Typography>
        </Box>
      ) : (
        <OrdersTable orders={filteredOrders} onOpen={handleOpenComplaint} />
      )}
    </Box>
  );
};

const ComplaintsTab = ({ agentCode }) => {
  const { instance, accounts } = useMsal();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchComplaints = useCallback(async () => {
    if (!agentCode) return;
    setLoading(true);
    setError(null);
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/agent-complaints/status?agentCode=${agentCode}`,
        { headers: { Authorization: `Bearer ${result.accessToken}` } },
      );
      if (!res.ok) throw new Error("שגיאה בטעינת התלונות");
      const data = await res.json();
      setComplaints(Array.isArray(data.complaints) ? data.complaints : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [agentCode, instance, accounts]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={fetchComplaints}>
            נסה שוב
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (complaints.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <AssignmentIcon
          sx={{ fontSize: 52, color: "text.disabled", mb: 1.5 }}
        />
        <Typography color="text.secondary">לא נמצאו תלונות</Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              מספר קריאה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              תאריך פתיחה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              סוג תלונה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              סיבת קריאה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              סטטוס טיפול
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              תיאור סגירה
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: direction_style }}>
              עדכון אחרון
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {complaints.map((c, idx) => {
            const isClosed = c.status === "closed";
            return (
              <TableRow
                key={c.complaintId || idx}
                hover
                sx={isClosed ? { backgroundColor: "#e8f5e9" } : {}}
              >
                <TableCell sx={{ textAlign: direction_style }}>
                  {c.complaintId || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {formatDate(c.openedAt)}
                </TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {c.complaintType || "-"}
                </TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {c.callReason || "-"}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: direction_style,
                    fontWeight: isClosed ? "bold" : "normal",
                    color: isClosed ? "#2e7d32" : "inherit",
                  }}
                >
                  {c.status || "-"}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: direction_style,
                    color: isClosed ? "#2e7d32" : "text.secondary",
                  }}
                >
                  {isClosed ? (c.descriptionClose || "-") : "-"}
                </TableCell>
                <TableCell sx={{ textAlign: direction_style }}>
                  {formatDate(c.lastUpdated)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AgentComplaintsPage = () => {
  const { agentCode, orders, ordersLoading, ordersError } = useAgent();
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 10, sm: 11 }, direction: "ltr" }}>
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{ mb: 2, color: "#0563B0", direction: "ltr" }}
      >
        תלונות סוכן
      </Typography>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid rgba(0,0,0,0.1)",
          borderRadius: 2,
          direction: "ltr",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)", px: 2 }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="פתיחת תלונה" />
          <Tab label="סטטוס תלונה" />
        </Tabs>

        <Box sx={{ p: 0, direction: "ltr" }}>
          {tab === 0 && (
            <OrdersTab
              orders={orders}
              ordersLoading={ordersLoading}
              ordersError={ordersError}
            />
          )}
          {tab === 1 && <ComplaintsTab agentCode={agentCode} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AgentComplaintsPage;
